/**
 * 回拨电话模块
 *
 * 功能：国家代码 + 手机号输入表单，提交后自动创建线索并回拨。
 *
 * Phase 2：对接后端 API（验证码 + 回拨任务创建）。
 */

import { setPanelContent, getShadowRoot } from "../../ui/container";
import { t } from "../../ui/i18n";
import { storage } from "../../core/storage";
import { collectVisitorInfo } from "../../core/visitor-info";
import type { LeadPayload, Locale } from "../../types";

/* ────── 国家代码列表 ────── */
const COUNTRY_CODES = [
  { code: "CN", dialCode: "86", flag: "🇨🇳" },
  { code: "US", dialCode: "1", flag: "🇺🇸" },
  { code: "GB", dialCode: "44", flag: "🇬🇧" },
  { code: "JP", dialCode: "81", flag: "🇯🇵" },
  { code: "KR", dialCode: "82", flag: "🇰🇷" },
  { code: "FR", dialCode: "33", flag: "🇫🇷" },
  { code: "DE", dialCode: "49", flag: "🇩🇪" },
  { code: "ES", dialCode: "34", flag: "🇪🇸" },
  { code: "RU", dialCode: "7", flag: "🇷🇺" },
  { code: "IN", dialCode: "91", flag: "🇮🇳" },
  { code: "BR", dialCode: "55", flag: "🇧🇷" },
  { code: "AU", dialCode: "61", flag: "🇦🇺" },
  { code: "CA", dialCode: "1", flag: "🇨🇦" },
  { code: "SG", dialCode: "65", flag: "🇸🇬" },
  { code: "HK", dialCode: "852", flag: "🇭🇰" },
  { code: "TW", dialCode: "886", flag: "🇹🇼" },
  { code: "MY", dialCode: "60", flag: "🇲🇾" },
  { code: "TH", dialCode: "66", flag: "🇹🇭" },
  { code: "VN", dialCode: "84", flag: "🇻🇳" },
];

/* ────── 步骤 ────── */
type Step = "form" | "verify" | "success";

/* ────── 主渲染 ────── */
export function renderCallbackPanel(locale: Locale, widgetId?: string): void {
  const savedPhone = storage.getPhone();
  const savedCountry = storage.getCountryCode();

  const html = /* html */ `
<div class="oc-callback oc-cb-root" id="oc-callback-root">
  <!-- Step 1: 手机号表单 -->
  <div id="oc-cb-step-form">
    <label class="oc-cb-label">${t("callback.phoneLabel", locale)}</label>

    <div class="oc-cb-phone-row">
      <div class="oc-cb-country-select" id="oc-cb-country-trigger">
        <span id="oc-cb-country-flag">${getFlag(savedCountry)}</span>
        <span id="oc-cb-country-code">+${getDialCode(savedCountry)}</span>
        <span style="font-size:10px;color:var(--oc-text-secondary);">▼</span>
      </div>
      <input
        type="tel"
        id="oc-cb-phone"
        class="oc-cb-phone-input"
        placeholder="${t("callback.phonePlaceholder", locale)}"
        value="${escapeHtml(savedPhone)}"
        autocomplete="tel-national"
      />
    </div>

    <!-- 国家代码下拉 -->
    <div class="oc-cb-country-dropdown" id="oc-cb-country-dropdown" style="display:none;">
      ${COUNTRY_CODES.map((c) => `
        <div class="oc-cb-country-option" data-code="${c.code}" data-dial="${c.dialCode}">
          <span>${c.flag}</span>
          <span>${c.dialCode}</span>
          <span style="font-size:12px;color:var(--oc-text-secondary);">${c.code}</span>
        </div>
      `).join("")}
    </div>

    <!-- 邮箱 -->
    <input
      type="email"
      id="oc-cb-email"
      class="oc-cb-input"
      placeholder="${t("callback.emailPlaceholder", locale)}"
      autocomplete="email"
    />

    <!-- 留言 -->
    <textarea
      id="oc-cb-message"
      class="oc-cb-textarea"
      rows="2"
      placeholder="${t("callback.messagePlaceholder", locale)}"
    ></textarea>

    <button type="button" class="oc-cb-submit" id="oc-cb-send">
      ${t("callback.sendCode", locale)}
    </button>

    <p class="oc-cb-hint">${t("callback.hint", locale)}</p>
  </div>

  <!-- Step 2: 验证码 -->
  <div id="oc-cb-step-verify" style="display:none;">
    <p class="oc-cb-verify-title">${t("callback.verifyTitle", locale)}</p>
    <p class="oc-cb-verify-info" id="oc-cb-verify-info">
      ${t("callback.verifyInfo", locale).replace("%s", `+${getDialCode(savedCountry)}`).replace("%s", (savedPhone || "****").slice(-4))}
    </p>

    <div class="oc-cb-code-row" id="oc-cb-code-row">
      ${Array.from({ length: 6 }, (_, i) => `
        <input type="text" maxlength="1" class="oc-cb-code-input" data-idx="${i}" />
      `).join("")}
    </div>

    <button type="button" class="oc-cb-resend" id="oc-cb-resend">
      ${t("callback.resend", locale)}
    </button>

    <button type="button" class="oc-cb-submit" id="oc-cb-confirm">
      📞 ${t("callback.confirm", locale)}
    </button>

    <p class="oc-cb-error" id="oc-cb-error" style="display:none;"></p>
  </div>

  <!-- Step 3: 成功 -->
  <div id="oc-cb-step-success" style="display:none;text-align:center;padding:24px 0;">
    <div style="font-size:48px;margin-bottom:16px;">✅</div>
    <p style="font-size:16px;font-weight:600;color:var(--oc-text);margin-bottom:8px;">
      ${t("callback.success", locale)}
    </p>
    <p style="font-size:13px;color:var(--oc-text-secondary);">
      我们将很快回拨到 <strong id="oc-cb-success-phone"></strong>
    </p>
  </div>
</div>

<style>
  .oc-cb-root { padding: 16px; }
  .oc-cb-label { display:block;font-size:14px;font-weight:600;margin-bottom:8px;color:var(--oc-text); }
  .oc-cb-phone-row { display:flex;gap:8px;margin-bottom:12px;position:relative; }
  .oc-cb-country-select {
    display:flex;align-items:center;gap:4px;padding:10px 12px;border:1px solid var(--oc-border);
    border-radius:8px;cursor:pointer;background:var(--oc-bg);font-size:14px;white-space:nowrap;
    min-width:80px;color:var(--oc-text);transition:border-color 0.15s;
  }
  .oc-cb-country-select:hover { border-color:var(--oc-primary); }
  .oc-cb-phone-input {
    flex:1;padding:10px 12px;border:1px solid var(--oc-border);border-radius:8px;
    font-size:14px;background:var(--oc-bg);color:var(--oc-text);
  }
  .oc-cb-phone-input:focus { outline:none;border-color:var(--oc-primary); }
  .oc-cb-country-dropdown {
    position:absolute;top:48px;left:0;width:220px;max-height:200px;overflow-y:auto;
    background:var(--oc-bg);border:1px solid var(--oc-border);border-radius:8px;
    box-shadow:0 8px 24px rgba(0,0,0,0.12);z-index:10;
  }
  .oc-cb-country-option {
    display:flex;align-items:center;gap:8px;padding:8px 12px;cursor:pointer;font-size:14px;
  }
  .oc-cb-country-option:hover { background:#f8f9ff; }
  .oc-cb-input {
    width:100%;padding:10px 12px;border:1px solid var(--oc-border);border-radius:8px;
    font-size:14px;margin-bottom:12px;background:var(--oc-bg);color:var(--oc-text);
  }
  .oc-cb-textarea {
    width:100%;padding:10px 12px;border:1px solid var(--oc-border);border-radius:8px;
    font-size:14px;resize:none;margin-bottom:16px;background:var(--oc-bg);color:var(--oc-text);
  }
  .oc-cb-submit {
    width:100%;padding:14px;border:none;border-radius:12px;font-size:16px;font-weight:600;
    color:#fff;cursor:pointer;background:var(--oc-primary);transition:all 0.15s;
  }
  .oc-cb-submit:hover { background:var(--oc-primary-hover); }
  .oc-cb-submit:disabled { opacity:0.5;cursor:not-allowed; }
  .oc-cb-hint { text-align:center;font-size:12px;margin-top:16px;color:var(--oc-text-secondary); }
  .oc-cb-verify-title { font-size:16px;font-weight:600;color:var(--oc-text);margin-bottom:8px; }
  .oc-cb-verify-info { font-size:13px;color:var(--oc-text-secondary);margin-bottom:20px;line-height:1.5; }
  .oc-cb-code-row { display:flex;gap:8px;margin-bottom:20px;justify-content:center; }
  .oc-cb-code-input {
    width:44px;height:52px;text-align:center;font-size:20px;font-weight:700;
    border:1px solid var(--oc-border);border-radius:8px;background:var(--oc-bg);color:var(--oc-text);
  }
  .oc-cb-code-input:focus { outline:none;border-color:var(--oc-primary); }
  .oc-cb-resend {
    border:none;background:none;font-size:12px;color:var(--oc-primary);cursor:pointer;
    padding:0;margin-bottom:16px;text-align:center;display:block;width:100%;
  }
  .oc-cb-error { font-size:12px;color:#ef4444;text-align:center;margin-top:8px; }
</style>
`;

  setPanelContent(html);
  bindCallbackEvents(locale, widgetId || "");
}

/* ────── 事件绑定 ────── */
function bindCallbackEvents(locale: Locale, widgetId: string): void {
  const root = getShadowRoot();
  if (!root) return;

  // 国家选择器
  let selectedCountry = storage.getCountryCode() || "CN";
  const trigger = root.getElementById("oc-cb-country-trigger");
  const dropdown = root.getElementById("oc-cb-country-dropdown");
  const flagEl = root.getElementById("oc-cb-country-flag");
  const codeEl = root.getElementById("oc-cb-country-code");

  trigger?.addEventListener("click", () => {
    if (dropdown) dropdown.style.display = dropdown.style.display === "none" ? "block" : "none";
  });

  dropdown?.addEventListener("click", (e) => {
    const target = (e.target as HTMLElement).closest(".oc-cb-country-option") as HTMLElement;
    if (!target) return;
    selectedCountry = target.dataset.code || "CN";
    const dial = target.dataset.dial || "+86";
    if (flagEl) flagEl.textContent = getFlag(selectedCountry);
    if (codeEl) codeEl.textContent = dial;
    if (dropdown) dropdown.style.display = "none";
    storage.setCountryCode(selectedCountry);
  });

  // 关闭下拉
  document.addEventListener("click", (e) => {
    if (!trigger?.contains(e.target as Node) && !dropdown?.contains(e.target as Node)) {
      if (dropdown) dropdown.style.display = "none";
    }
  });

  // Step 1 → Step 2
  const sendBtn = root.getElementById("oc-cb-send");
  const phoneInput = root.getElementById("oc-cb-phone") as HTMLInputElement;
  const emailInput = root.getElementById("oc-cb-email") as HTMLInputElement;
  const messageInput = root.getElementById("oc-cb-message") as HTMLTextAreaElement;

  sendBtn?.addEventListener("click", () => {
    const phone = phoneInput?.value.trim() || "";
    if (!phone) {
      alert("请输入手机号");
      return;
    }
    storage.setPhone(phone);

    // 模拟发送验证码
    const formStep = root.getElementById("oc-cb-step-form");
    const verifyStep = root.getElementById("oc-cb-step-verify");
    const verifyInfo = root.getElementById("oc-cb-verify-info");
    if (formStep) formStep.style.display = "none";
    if (verifyStep) verifyStep.style.display = "block";
    if (verifyInfo) {
      verifyInfo.textContent = t("callback.verifyInfo", locale)
        .replace("%s", `+${getDialCode(selectedCountry)}`)
        .replace("%s", phone.slice(-4));
    }
  });

  // Step 2: 验证码输入
  const codeInputs = root.querySelectorAll(".oc-cb-code-input") as NodeListOf<HTMLInputElement>;
  codeInputs.forEach((inp, i) => {
    inp.addEventListener("input", () => {
      const v = inp.value.replace(/[^0-9]/g, "");
      inp.value = v.slice(0, 1);
      if (v && i < 5) codeInputs[i + 1]?.focus();
    });
    inp.addEventListener("keydown", (e) => {
      if (e.key === "Backspace" && !inp.value && i > 0) {
        codeInputs[i - 1]?.focus();
      }
    });
  });

  // 重发验证码
  const resendBtn = root.getElementById("oc-cb-resend");
  resendBtn?.addEventListener("click", () => {
    if ((resendBtn as HTMLButtonElement).disabled) return;
    (resendBtn as HTMLButtonElement).disabled = true;
    resendBtn.textContent = "发送中...";
    setTimeout(() => {
      (resendBtn as HTMLButtonElement).disabled = false;
      resendBtn.textContent = t("callback.resend", locale);
    }, 3000);
  });

  // Step 2 → Step 3: 确认回拨
  const confirmBtn = root.getElementById("oc-cb-confirm");
  confirmBtn?.addEventListener("click", async () => {
    const code = Array.from(codeInputs).map((i) => i.value).join("");
    if (code.length < 6) {
      const errEl = root.getElementById("oc-cb-error");
      if (errEl) { errEl.style.display = "block"; errEl.textContent = "请输入完整验证码"; }
      return;
    }

    (confirmBtn as HTMLButtonElement).disabled = true;
    confirmBtn.textContent = "提交中...";

    try {
      // 自动采集访客信息
      const visitorInfo = collectVisitorInfo();
      const phone = (phoneInput?.value || "").trim();
      const email = emailInput?.value?.trim() || "";
      const message = messageInput?.value?.trim() || "";

      // 提交线索到后端
      const apiBase = (window as any).__ONECALL_API_BASE__ || "/api";
      const res = await fetch(`${apiBase}/widget/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          widgetId: widgetId || "",
          name: "",
          phone,
          email,
          pageUrl: visitorInfo.pageUrl,
          utmParams: visitorInfo.utm,
          device: visitorInfo.deviceJson,
          browser: visitorInfo.browserJson,
          actionType: "callback",
          actionLabel: "回拨请求",
          callbackMessage: message,
          metadata: { visitorId: visitorInfo.visitorId, countryCode: selectedCountry, referrer: visitorInfo.referrer },
        }),
      });

      if (!res.ok) {
        let serverMsg = "";
        try { const errBody = await res.json(); serverMsg = errBody?.error || ""; } catch {}
        throw new Error(serverMsg || `提交失败 (${res.status})`);
      }

      // 提交成功 → 显示成功页
      const verifyStep = root.getElementById("oc-cb-step-verify");
      const successStep = root.getElementById("oc-cb-step-success");
      const successPhone = root.getElementById("oc-cb-success-phone");
      if (verifyStep) verifyStep.style.display = "none";
      if (successStep) successStep.style.display = "block";
      if (successPhone) {
        successPhone.textContent = `+${getDialCode(selectedCountry)} ${phone}`;
      }
    } catch (err: any) {
      const errEl = root.getElementById("oc-cb-error");
      if (errEl) {
        errEl.style.display = "block";
        errEl.textContent = err?.message || "提交失败，请重试";
      }
      (confirmBtn as HTMLButtonElement).disabled = false;
      confirmBtn.textContent = "📞 " + t("callback.confirm", locale);
    }
  });
}

/* ────── 辅助 ────── */
function getFlag(code: string): string {
  return COUNTRY_CODES.find((c) => c.code === code)?.flag || "🌍";
}

function getDialCode(code: string): string {
  return COUNTRY_CODES.find((c) => c.code === code)?.dialCode || "86";
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");
}
